/**
 * 客户端 Logger 配置（@cxcxcx/client-logger）
 * 适用于本项目：Vue 2 + vue-router 3 + axios + Element UI
 *
 * 注意：本文件会被 request → api → store/modules 间接引用，与 store 存在循环依赖。
 * 禁止顶层 import store；统一通过 getStore() 延迟获取。
 */
import axios from 'axios'
import { createClientLogger } from '@cxcxcx/client-logger'
import { setupVue2ErrorHandler } from '@cxcxcx/client-logger/vue2'
import { setupRouterLogger, setupClickTrack } from '@cxcxcx/client-logger/vue-router'
import { getToken } from '@/utils/auth'

const APP_VERSION = process.env.VUE_APP_VERSION || require('../../package.json').version
const API_BASE = process.env.VUE_APP_BASE_API || ''
const runEnv = process.env.ENV || process.env.NODE_ENV || ''

const CLIENT_LOG_UPLOAD_PATH = '/ja/clientLog/batch'
const DEFAULT_FLUSH_INTERVAL_MS = 60000

/** 延迟取 store，避开循环依赖导致的 undefined */
function getStore() {
  // eslint-disable-next-line global-require
  return require('@/store').default
}

/** 是否开启日志：VUE_APP_LOG_ENABLED 优先；未配置时仅 development 开启（登录后再真正挂载） */
export function resolveLogEnabled() {
  const flag = process.env.VUE_APP_LOG_ENABLED
  if (flag === 'true') return true
  if (flag === 'false') return false
  return process.env.NODE_ENV === 'development'
}

export const isLogEnabled = resolveLogEnabled()

function resolveLogFlushInterval() {
  const fromEnv = Number(process.env.VUE_APP_LOG_FLUSH_INTERVAL)
  if (Number.isFinite(fromEnv) && fromEnv > 0) return fromEnv

  try {
    const fromStorage = Number(window.localStorage.getItem('ja_log_flush_interval'))
    if (Number.isFinite(fromStorage) && fromStorage > 0) return fromStorage
  } catch (error) {
    // ignore
  }

  return DEFAULT_FLUSH_INTERVAL_MS
}

function resolveClientUserId() {
  const vuexStore = getStore()
  if (!vuexStore || !vuexStore.state) return ''
  const userInfo = vuexStore.state.userInfo
  return (
    (userInfo &&
      (userInfo.clientUserId || userInfo.userId || userInfo.id || userInfo.username)) ||
    ''
  )
}

export const logger = createClientLogger({
  appVersion: APP_VERSION,
  runEnv,
  platform: 'desktop',
  enabled: isLogEnabled,
  flushInterval: resolveLogFlushInterval(),
  getFlushInterval: resolveLogFlushInterval,
  uploadBaseURL: API_BASE,
  uploadPath: CLIENT_LOG_UPLOAD_PATH,
  dbName: 'ja_client_log',
  legacyStorageKey: 'ja_client_log_pending',
  getAuthToken: getToken,
  getClientUserId: resolveClientUserId,
  createHttpClient: () => axios.create({ baseURL: API_BASE, timeout: 20000, data: { appCode: 'JA' } }),
  uiLibraries: ['element-ui']
})

let installed = false
/** 路由/点击/Vue 错误钩子只绑定一次（logger.enabled=false 时内部会直接 no-op） */
let hooksBound = false

/**
 * 在应用入口初始化 Logger（错误捕获 / 路由 page_view / 全局点击）
 * 环境允许开启时：等登录 / session 已有 token 后再真正挂载监控；退出登录后关闭
 */
export async function setupAppLogger(Vue, router) {
  if (!isLogEnabled) {
    await logger.init({ uploadBaseURL: API_BASE, enabled: false })
    return
  }

  const vuexStore = getStore()

  const bindHooksOnce = () => {
    if (hooksBound) return
    hooksBound = true
    setupVue2ErrorHandler(logger, Vue)
    setupRouterLogger(router, logger)
    setupClickTrack(router, logger, {
      skipTarget(target) {
        return Boolean(target && target.closest && target.closest('input[type="password"]'))
      }
    })
  }

  const mount = async() => {
    if (installed) return
    // 在 await 前占位，避免重复挂载与 require-atomic-updates
    installed = true
    // destroy 后才能再次 init 切换 enabled（库内部 initialized 门闩）
    logger.destroy()
    await logger.init({ uploadBaseURL: API_BASE, enabled: true })
    bindHooksOnce()
  }

  const unmount = async() => {
    if (!installed) return
    installed = false
    logger.destroy()
    await logger.init({ uploadBaseURL: API_BASE, enabled: false })
  }

  // 登录挂载 / 退出卸载（以 vuex token 为准；本项目 token 在根 state，无 user 模块）
  if (vuexStore && typeof vuexStore.watch === 'function') {
    vuexStore.watch(
      state => state.token,
      token => {
        if (token) mount()
        else unmount()
      }
    )
  }

  if (vuexStore && vuexStore.state && vuexStore.state.token) {
    await mount()
  } else {
    await logger.init({ uploadBaseURL: API_BASE, enabled: false })
  }
}
